import React, { useState, useEffect, useCallback, useMemo } from 'react';
import APIService from '../../services/api';
import ErpiaSelect from '../ErpiaSelect';
import { confirmErpia } from '../ConfirmDialog';

/**
 * Automatismes — sous-module du CRM.
 *
 * Le principe tient en une phrase : « quand ceci arrive, fais cela ». Pas de
 * canevas à brancher, pas de connecteurs externes — les déclencheurs et les
 * actions sont ceux d'erpia (CRA, facture, formulaire), servis par le serveur
 * (GET /automatismes/catalogue) et exécutés par lui.
 *
 * L'ÉCRAN EST ORGANISÉ PAR CE QU'ON VIENT Y FAIRE, pas par ce qu'il contient.
 * On ouvre Automatismes pour traiter ce qui attend une décision — pas pour
 * relire ses règles. « À décider » est donc en tête ; les règles viennent
 * après, et le journal en dernier.
 *
 * DEUX GARANTIES, tenues par le SERVEUR et pas par cet écran :
 *   1. une action qui SORT d'erpia (email au client) ou qui ne se reprend pas
 *      d'un clic est refusée en mode automatique — l'écran n'a pas à cacher
 *      l'option, le serveur la refuse ;
 *   2. une règle naît INACTIVE, et chaque déclenchement laisse une ligne.
 *
 * Routes (tolérées absentes : 404/501 → l'écran le dit et n'écrit rien) :
 *   GET /automatismes/catalogue · GET|POST /automatismes · PUT|DELETE /automatismes/:id
 *   GET /automatismes/journal · POST /automatismes/journal/:id/executer|ignorer
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const LBL = {
  fontFamily: 'var(--erp-font-mono)', fontSize: '9.5px', fontWeight: 700,
  letterSpacing: '.13em', textTransform: 'uppercase', color: 'var(--erp-text-tertiary)',
};
const absente = (r) => [404, 405, 501].includes(r.status);
const quand = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' })
    + ' à ' + d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};
const nomUser = (u) => `${u.prenom || ''} ${u.nom || ''}`.trim() || u.username || u.email || `#${u.id}`;
// Ce qui distingue DEUX comptes qui portent le même nom : l'email, unique
// dans l'organisation. On ne l'affiche que quand il sert — coller une adresse
// sous chaque nom d'une liste propre serait du bruit.
const signeUser = (u) => u.email || u.username || `#${u.id}`;

// L'état d'une ligne de journal, dit avec le mot qu'on emploierait à l'oral.
const ETATS = {
  propose: ['Attend votre décision', 'hourglass-split', 'var(--erp-accent)'],
  execute: ['Fait', 'check-lg', 'var(--erp-success)'],
  echec: ['A échoué', 'exclamation-triangle-fill', 'var(--erp-danger)'],
  ignore: ['Ignorée', 'slash-circle', 'var(--erp-text-tertiary)'],
};

export default function CRMAutomatismes({ showNotification }) {
  const [catalogue, setCatalogue] = useState(null);   // { declencheurs, actions, modes }
  const [regles, setRegles] = useState(null);         // null = chargement
  const [attente, setAttente] = useState([]);         // journal statut=propose
  const [histoire, setHistoire] = useState([]);       // journal complet
  const [users, setUsers] = useState([]);
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState('');
  const [form, setForm] = useState(null);             // règle en composition/édition
  const [champErreur, setChampErreur] = useState(null); // { champ, message }
  const [busy, setBusy] = useState(null);             // id ou 'form' en cours
  const [voirJournal, setVoirJournal] = useState(false);

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const r = await fetch(`${BASE}/automatismes`, { headers: authH() });
      if (absente(r)) { setAbsent(true); setRegles([]); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setRegles(Array.isArray(j.data) ? j.data : []);
    } catch (e) { setErreur(e.message); setRegles([]); }
  }, []);

  const chargerJournal = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([
        fetch(`${BASE}/automatismes/journal?statut=propose&limit=50`, { headers: authH() }),
        fetch(`${BASE}/automatismes/journal?limit=40`, { headers: authH() }),
      ]);
      if (a.ok) { const j = await a.json().catch(() => ({})); setAttente(Array.isArray(j.data) ? j.data : []); }
      if (h.ok) { const j = await h.json().catch(() => ({})); setHistoire(Array.isArray(j.data) ? j.data : []); }
    } catch { /* le journal est un confort : son absence ne casse pas l'écran */ }
  }, []);

  useEffect(() => {
    let annule = false;
    fetch(`${BASE}/automatismes/catalogue`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (!annule && j?.data) setCatalogue(j.data); })
      .catch(() => { /* sans catalogue, on n'invente pas de vocabulaire : la
                        composition reste fermée et l'écran le dit. */ });
    fetch(`${BASE}/users`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { const arr = j?.data || j?.users || (Array.isArray(j) ? j : []); if (!annule && Array.isArray(arr)) setUsers(arr); })
      .catch(() => {});
    return () => { annule = true; };
  }, []);
  useEffect(() => { charger(); chargerJournal(); }, [charger, chargerJournal]);

  const actionDe = useCallback((cle) => (catalogue?.actions || []).find(a => a.cle === cle) || null, [catalogue]);
  const declencheurDe = useCallback((cle) => (catalogue?.declencheurs || []).find(d => d.cle === cle) || null, [catalogue]);

  // Les actions PROPOSÉES pour le déclencheur choisi : c'est le serveur qui
  // dit lesquelles ont les informations pour s'exécuter. Laisser composer un
  // couple impossible, c'est écrire une règle qui n'échouera qu'un jour, sur
  // un vrai dossier.
  const actionsPossibles = useMemo(() => {
    const d = declencheurDe(form?.declencheur);
    if (!d) return [];
    return (d.actions || []).map(actionDe).filter(Boolean);
  }, [form, declencheurDe, actionDe]);
  const actionChoisie = actionDe(form?.action);

  // QUI ON PEUT PRÉVENIR. Deux comptes peuvent porter le même nom — homonymes,
  // ou un compte recréé après suppression. Un sélecteur qui affiche neuf fois
  // « Jihed BEN YEDDER » ne se choisit pas : dès qu'un libellé n'est pas
  // unique, l'email le désambiguïse, à côté du nom.
  //
  // Les comptes DÉSACTIVÉS sont écartés : les proposer, c'est armer une règle
  // qui préviendra quelqu'un qui ne se connecte plus. (Les comptes supprimés,
  // eux, ne sortent plus du serveur — c'est de là que venaient les doublons.)
  const destinatairesPossibles = useMemo(() => {
    const vivants = users.filter(u => u && u.active !== false && u.status !== 'deleted');
    const combien = new Map();
    for (const u of vivants) combien.set(nomUser(u), (combien.get(nomUser(u)) || 0) + 1);
    return vivants
      .map(u => ({ ...u, _nom: nomUser(u), _distinctif: combien.get(nomUser(u)) > 1 ? signeUser(u) : null }))
      .sort((a, b) => a._nom.localeCompare(b._nom, 'fr'));
  }, [users]);

  const ouvrirComposition = (regle = null) => {
    setChampErreur(null);
    setForm(regle
      ? { id: regle.id, nom: regle.nom || '', declencheur: regle.declencheur, action: regle.action,
          mode: regle.mode, params: regle.params || {} }
      : { nom: '', declencheur: '', action: '', mode: 'propose', params: {} });
  };

  const enregistrer = async () => {
    if (!form.declencheur || !form.action) {
      setChampErreur({ champ: form.declencheur ? 'action' : 'declencheur', message: 'Une règle a besoin d\'un déclencheur et d\'une action.' });
      return;
    }
    setBusy('form'); setChampErreur(null);
    try {
      const edition = form.id != null;
      const r = await fetch(`${BASE}/automatismes${edition ? `/${form.id}` : ''}`, {
        method: edition ? 'PUT' : 'POST', headers: jsonH(),
        body: JSON.stringify({ nom: form.nom || undefined, declencheur: form.declencheur,
          action: form.action, mode: form.mode, params: form.params || {} }),
      });
      const j = await r.json().catch(() => ({}));
      if (absente(r) && !j.error) { showNotification?.('Les automatismes arrivent côté serveur — rien n\'a été enregistré.', 'info'); return; }
      if (!r.ok || j.success === false) {
        // Le serveur DÉSIGNE le champ fautif (mode, action, params.destinataires) :
        // on l'accroche là où on corrige, pas dans une notification qui s'évapore.
        if (j.champ) { setChampErreur({ champ: String(j.champ), message: j.error || `HTTP ${r.status}` }); return; }
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      showNotification?.(j.message || (edition ? 'Règle mise à jour.' : 'Règle créée.'), 'success');
      setForm(null);
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  // ARMER une règle est le moment où elle commence à agir sur de vrais
  // dossiers : on le dit avant, avec ce qu'elle fera exactement.
  const basculer = async (regle) => {
    const a = actionDe(regle.action);
    if (!regle.actif) {
      const ok = await confirmErpia({
        title: 'Activer cette règle',
        message: `« ${regle.nom} » agira sur les prochains dossiers concernés.`,
        detail: regle.mode === 'auto'
          ? `${a?.libelle || regle.action_libelle} se fera SEUL, sans confirmation. Les faits déjà passés ne sont pas rattrapés.`
          : `${a?.libelle || regle.action_libelle} sera PRÉPARÉ et attendra votre clic ici. Rien ne partira sans décision.`,
        confirmLabel: 'Activer la règle',
      });
      if (!ok) return;
    }
    setBusy(regle.id);
    try {
      const r = await fetch(`${BASE}/automatismes/${regle.id}`, {
        method: 'PUT', headers: jsonH(), body: JSON.stringify({ actif: !regle.actif }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(regle.actif ? 'Règle désactivée.' : 'Règle activée.', 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const supprimer = async (regle) => {
    const ok = await confirmErpia({
      title: 'Retirer cette règle',
      message: `« ${regle.nom} » ne se déclenchera plus.`,
      detail: 'Ce qu\'elle a déjà fait reste au journal — on ne réécrit pas le passé.',
      confirmLabel: 'Retirer la règle', tone: 'danger',
    });
    if (!ok) return;
    setBusy(regle.id);
    try {
      const r = await fetch(`${BASE}/automatismes/${regle.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Règle retirée.', 'success');
      await charger(); await chargerJournal();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  // LE CLIC QUE « PROPOSÉ » PROMET. Pour une action sortante, on redemande :
  // c'est exactement la raison pour laquelle elle n'est pas partie seule.
  const executer = async (ligne) => {
    if (ligne.sortant || ligne.sensible) {
      const ok = await confirmErpia({
        title: ligne.action_libelle,
        message: ligne.resume,
        detail: ligne.sortant
          ? 'Un message quitte erpia et arrive chez un tiers.'
          : 'L\'effet ne se reprend pas d\'un clic.',
        confirmLabel: ligne.action_libelle,
      });
      if (!ok) return;
    }
    setBusy(`j${ligne.id}`);
    try {
      const r = await fetch(`${BASE}/automatismes/journal/${ligne.id}/executer`, { method: 'POST', headers: jsonH(), body: '{}' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Action exécutée.', 'success');
      await chargerJournal(); await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const ignorer = async (ligne) => {
    setBusy(`j${ligne.id}`);
    try {
      const r = await fetch(`${BASE}/automatismes/journal/${ligne.id}/ignorer`, { method: 'POST', headers: jsonH(), body: '{}' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Action ignorée.', 'success');
      await chargerJournal(); await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const majParam = (cle, valeur) => setForm(f => ({ ...f, params: { ...(f.params || {}), [cle]: valeur } }));
  const sur = (champ) => champErreur?.champ === champ;

  return (
    <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* ── Ce que c'est, et les deux garanties ─────────────────────────── */}
      <div className="crm-auto-carte crm-auto-carte--annonce">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
          <span style={{ width: '38px', height: '38px', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-accent-soft)', color: 'var(--erp-accent)', fontSize: '17px' }}>
            <BI icon="arrow-return-right" />
          </span>
          <div style={{ flex: 1, minWidth: '260px' }}>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>Automatismes</h2>
            <p style={{ margin: '7px 0 0', fontSize: '13px', lineHeight: 1.6, color: 'var(--erp-text-secondary)', maxWidth: '68ch' }}>
              Le travail répétitif du CRM se dit en une phrase : <strong style={{ color: 'var(--erp-ink)' }}>quand ceci arrive, fais cela</strong>.
              La facture qui se prépare dès que le client valide le CRA, le mois figé pour la compta,
              la relance qui part quand une facture est prête — sans que personne y pense.
            </p>
            <p style={{ margin: '9px 0 0', fontSize: '12.5px', lineHeight: 1.6, color: 'var(--erp-text-tertiary)', maxWidth: '68ch' }}>
              Ce qui <strong style={{ color: 'var(--erp-ink)' }}>sort</strong> d'erpia — un email au client, une facture envoyée — ne part jamais seul :
              la règle le prépare, vous cliquez. Et chaque déclenchement laisse une ligne au journal.
            </p>
          </div>
          {catalogue && !absent && !form && (
            <button type="button" className="erp-btn erp-btn--primary" onClick={() => ouvrirComposition()}>
              <BI icon="plus-lg" /> Nouvelle règle
            </button>
          )}
        </div>
      </div>

      {absent && (
        <div className="crm-auto-carte crm-auto-carte--note">
          <BI icon="hourglass-split" style={{ marginRight: '7px' }} />
          Les automatismes arrivent côté serveur. L'écran est prêt : vos règles aboutiront dès la mise en ligne, rien n'est enregistré d'ici là.
        </div>
      )}
      {erreur && (
        <div role="alert" className="crm-auto-carte crm-auto-carte--erreur">{erreur}</div>
      )}

      {/* ── CE QUI ATTEND VOTRE DÉCISION — en tête, c'est le travail ────── */}
      {attente.length > 0 && (
        <div className="crm-auto-carte crm-auto-carte--attente">
          <div style={{ ...LBL, marginBottom: '13px', color: 'var(--erp-accent)' }}>
            À décider · {attente.length}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            {attente.map(l => (
              <div key={l.id} className="crm-auto-ligne">
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink-strong)' }}>{l.action_libelle}</div>
                  <div style={{ marginTop: '2px', fontSize: '12px', color: 'var(--erp-text-secondary)' }}>{l.resume}</div>
                  <div style={{ marginTop: '3px', fontSize: '11px', color: 'var(--erp-text-tertiary)' }}>
                    {l.regle ? `Règle « ${l.regle} »` : l.declencheur_libelle}{quand(l.cree_le) ? ` · préparée le ${quand(l.cree_le)}` : ''}
                  </div>
                </div>
                {l.sortant && <span className="erp-chip" title="Un message quitte erpia"><BI icon="box-arrow-up-right" /> Sortant</span>}
                {l.sensible && <span className="erp-chip" title="Ne se reprend pas d'un clic"><BI icon="lock" /> Irréversible</span>}
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => ignorer(l)} disabled={busy === `j${l.id}`}>
                  Ignorer
                </button>
                <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => executer(l)} disabled={busy === `j${l.id}`}>
                  <BI icon={busy === `j${l.id}` ? 'arrow-repeat' : 'check-lg'} style={busy === `j${l.id}` ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
                  {l.action_libelle}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Composer une règle ───────────────────────────────────────────── */}
      {form && catalogue && (
        <div className="crm-auto-carte">
          <div style={{ ...LBL, marginBottom: '14px' }}>{form.id != null ? 'Modifier la règle' : 'Nouvelle règle'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '5px', ...LBL, letterSpacing: '.06em' }}>
              Quand
              <ErpiaSelect value={form.declencheur} aria-label="Déclencheur"
                style={sur('declencheur') ? { borderColor: 'var(--danger-bd)' } : undefined}
                onChange={e => { setChampErreur(null); setForm(f => ({ ...f, declencheur: e.target.value, action: '' })); }}>
                <option value="">Choisissez ce qui déclenche…</option>
                {catalogue.declencheurs.map(d => <option key={d.cle} value={d.cle}>{d.libelle}</option>)}
              </ErpiaSelect>
              {declencheurDe(form.declencheur)?.description && (
                <small className="crm-auto-aide">{declencheurDe(form.declencheur).description}</small>
              )}
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '5px', ...LBL, letterSpacing: '.06em' }}>
              Alors
              <ErpiaSelect value={form.action} aria-label="Action" disabled={!form.declencheur}
                style={sur('action') ? { borderColor: 'var(--danger-bd)' } : undefined}
                onChange={e => {
                  const cle = e.target.value; const a = actionDe(cle);
                  setChampErreur(null);
                  // Le mode s'aligne SEUL sur ce que l'action autorise : proposer
                  // « automatique » puis le refuser à l'enregistrement serait
                  // faire découvrir la règle par un échec.
                  setForm(f => ({ ...f, action: cle, mode: a && !a.auto_possible ? 'propose' : f.mode }));
                }}>
                <option value="">{form.declencheur ? 'Choisissez ce qui se passe…' : 'Choisissez d\'abord un déclencheur'}</option>
                {actionsPossibles.map(a => <option key={a.cle} value={a.cle}>{a.libelle}</option>)}
              </ErpiaSelect>
              {actionChoisie?.description && (
                <small className="crm-auto-aide">{actionChoisie.description}</small>
              )}
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <span style={LBL}>Comment</span>
              <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                {catalogue.modes.map(m => {
                  const interdit = m.cle === 'auto' && actionChoisie && !actionChoisie.auto_possible;
                  const actif = form.mode === m.cle;
                  return (
                    <button key={m.cle} type="button" className="crm-auto-choix"
                      disabled={interdit} data-actif={actif ? 'true' : undefined}
                      aria-pressed={actif}
                      title={interdit ? actionChoisie.raison_mode : m.description}
                      onClick={() => { setChampErreur(null); setForm(f => ({ ...f, mode: m.cle })); }}>
                      {m.libelle}
                    </button>
                  );
                })}
              </div>
              {/* Un mode grisé sans raison ressemble à une limitation du
                  logiciel : on dit pourquoi, à l'endroit où on le découvre. */}
              {actionChoisie && !actionChoisie.auto_possible && (
                <small className="crm-auto-aide">{actionChoisie.raison_mode}</small>
              )}
              {sur('mode') && <small className="crm-auto-aide" data-erreur="true" role="alert">{champErreur.message}</small>}
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '5px', ...LBL, letterSpacing: '.06em' }}>
              Nom de la règle
              <input className="erp-field" value={form.nom} placeholder={form.declencheur && form.action ? 'Repris de la phrase ci-dessus' : 'Facultatif'}
                onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} />
            </label>
          </div>

          {/* Paramètres propres à l'action — servis par le catalogue. */}
          {(actionChoisie?.params || []).map(p => (
            <div key={p.cle} style={{ marginTop: '14px' }}>
              <div style={{ ...LBL, marginBottom: '7px' }}>{p.libelle}{p.obligatoire ? ' *' : ''}</div>
              {p.type === 'utilisateurs' ? (
                destinatairesPossibles.length ? (
                  <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                    {destinatairesPossibles.map(u => {
                      const choisi = (form.params?.[p.cle] || []).map(Number).includes(Number(u.id));
                      return (
                        <button key={u.id} type="button" aria-pressed={choisi}
                          title={signeUser(u)}
                          aria-label={`${u._nom} — ${signeUser(u)}`}
                          className="crm-auto-choix crm-auto-choix--pastille" data-actif={choisi ? 'true' : undefined}
                          onClick={() => {
                            setChampErreur(null);
                            const actuels = (form.params?.[p.cle] || []).map(Number);
                            majParam(p.cle, choisi ? actuels.filter(x => x !== Number(u.id)) : [...actuels, Number(u.id)]);
                          }}>
                          {choisi && <BI icon="check-lg" style={{ marginRight: '5px' }} />}{u._nom}
                          {u._distinctif && <span className="crm-auto-distinctif">{u._distinctif}</span>}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--erp-text-tertiary)' }}>
                    {users.length
                      ? 'Aucun compte actif à prévenir — les comptes désactivés ne reçoivent rien.'
                      : "La liste des utilisateurs n'a pas pu être lue — rechargez la page pour choisir qui prévenir."}
                  </div>
                )
              ) : (
                <input className="erp-field" value={form.params?.[p.cle] ?? ''} onChange={e => majParam(p.cle, e.target.value)} />
              )}
              {sur(`params.${p.cle}`) && <small role="alert" style={{ display: 'block', marginTop: '5px', fontSize: '11px', color: 'var(--danger-fg)' }}>{champErreur.message}</small>}
            </div>
          ))}

          {champErreur && !['declencheur', 'action', 'mode'].includes(champErreur.champ) && !/^params\./.test(champErreur.champ) && (
            <div role="alert" style={{ marginTop: '12px', fontSize: '12px', color: 'var(--danger-fg)' }}>{champErreur.message}</div>
          )}

          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: '200px', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--erp-text-secondary)' }}>
              {form.id != null
                ? 'La règle garde son état : modifier ne l\'active pas.'
                : 'La règle sera créée INACTIVE. Relisez-la, puis activez-la quand vous êtes prêt.'}
            </span>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={() => { setForm(null); setChampErreur(null); }}>Annuler</button>
            <button type="button" className="erp-btn erp-btn--primary" onClick={enregistrer} disabled={busy === 'form'}>
              {busy === 'form' ? 'Enregistrement…' : form.id != null ? 'Enregistrer les modifications' : 'Créer la règle'}
            </button>
          </div>
        </div>
      )}

      {/* ── Les règles ───────────────────────────────────────────────────── */}
      <div className="crm-auto-carte">
        <div style={{ ...LBL, marginBottom: '14px' }}>Vos règles</div>
        {regles === null ? (
          <div style={{ fontSize: '12.5px', color: 'var(--erp-text-tertiary)' }}>
            <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite', marginRight: '6px' }} />Chargement…
          </div>
        ) : regles.length === 0 ? (
          <div style={{ padding: '26px 6px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--erp-ink)' }}>Aucune règle pour l'instant — rien ne se déclenche.</p>
            <p style={{ margin: '6px auto 14px', maxWidth: '54ch', fontSize: '12.5px', lineHeight: 1.55, color: 'var(--erp-text-secondary)' }}>
              La première est toute trouvée : quand le client valide le CRA du mois, préparer la facture correspondante en brouillon.
            </p>
            {catalogue && !absent && (
              <button type="button" className="erp-btn erp-btn--primary"
                onClick={() => setForm({ nom: '', declencheur: 'cra_valide_client', action: 'facture_depuis_cra', mode: 'auto', params: {} })}>
                Composer cette règle
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {regles.map(r => (
              <div key={r.id} className="crm-auto-regle"
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-sm)', flexWrap: 'wrap', opacity: r.actif ? 1 : 0.7 }}>
                <span style={{ flex: '1 1 240px', minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ ...LBL, flex: 'none', width: '38px' }}>Quand</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink-strong)' }}>{r.declencheur_libelle}</span>
                </span>
                {/* Signature de l'écran : le raccord de la chaîne, seul point de couleur. */}
                <span className="crm-auto-lien" aria-hidden="true" style={{ flex: 'none' }} />
                <span style={{ flex: '1 1 230px', minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ ...LBL, flex: 'none', width: '34px' }}>Alors</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13px', color: 'var(--erp-ink)' }}>{r.action_libelle}</span>
                    <span style={{ display: 'block', marginTop: '2px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>
                      {r.executions ? `${r.executions} fois` : 'jamais déclenchée'}
                      {r.en_attente ? ` · ${r.en_attente} en attente` : ''}
                    </span>
                  </span>
                </span>
                <span className="erp-chip" style={r.mode === 'propose' ? { borderColor: 'var(--erp-accent)', color: 'var(--erp-accent)', background: 'var(--erp-accent-soft)' } : undefined}>
                  <BI icon={r.mode === 'auto' ? 'lightning-charge' : 'hand-index'} /> {r.mode === 'auto' ? 'Automatique' : 'Proposé'}
                </span>
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => basculer(r)} disabled={busy === r.id}
                  title={r.actif ? 'La règle cessera de se déclencher.' : 'La règle agira sur les prochains dossiers.'}>
                  <BI icon={r.actif ? 'pause-fill' : 'play-fill'} />{r.actif ? 'Active' : 'Inactive'}
                </button>
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => ouvrirComposition(r)} aria-label={`Modifier ${r.nom}`}>
                  <BI icon="pencil" />
                </button>
                <button type="button" className="erp-btn erp-btn--danger erp-btn--sm" onClick={() => supprimer(r)} disabled={busy === r.id} aria-label={`Retirer ${r.nom}`}>
                  <BI icon="trash3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Le journal — dernier, mais toujours là ───────────────────────── */}
      {histoire.length > 0 && (
        <div className="crm-auto-carte">
          <button type="button" className="crm-auto-repli" aria-expanded={voirJournal} onClick={() => setVoirJournal(v => !v)}>
            <span style={LBL}>Journal · {histoire.length} dernier(s) déclenchement(s)</span>
            <span style={{ flex: 1 }} />
            <BI icon={voirJournal ? 'chevron-up' : 'chevron-down'} style={{ color: 'var(--erp-text-tertiary)' }} />
          </button>
          {voirJournal && (
            <div style={{ marginTop: '13px', display: 'flex', flexDirection: 'column' }}>
              {histoire.map(l => {
                const [etat, icone, couleur] = ETATS[l.statut] || [l.statut, 'dot', 'var(--erp-text-tertiary)'];
                return (
                  <div key={l.id} className="crm-auto-journal-ligne">
                    <span style={{ flex: 'none', width: '18px', color: couleur }}><BI icon={icone} /></span>
                    <span style={{ flex: '1 1 300px', minWidth: 0, fontSize: '12.5px', color: 'var(--erp-ink)' }}>
                      {l.resume} <span style={{ color: 'var(--erp-text-tertiary)' }}>→ {l.action_libelle}</span>
                      {/* L'erreur du serveur est affichée TELLE QUELLE : c'est
                          elle qui dit quoi corriger, pas un « une erreur est
                          survenue » qui n'aide personne. */}
                      {l.erreur && <span style={{ display: 'block', marginTop: '2px', fontSize: '11.5px', color: 'var(--danger-fg)' }}>{l.erreur}</span>}
                      {l.resultat_resume && <span style={{ display: 'block', marginTop: '2px', fontSize: '11.5px', color: 'var(--erp-text-secondary)' }}>{l.resultat_resume}</span>}
                    </span>
                    <span className="erp-chip" style={{ flex: 'none' }}>{etat}</span>
                    <span style={{ flex: 'none', fontFamily: 'var(--erp-font-mono)', fontSize: '10.5px', color: 'var(--erp-text-tertiary)' }}>
                      {quand(l.traite_le || l.cree_le)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
